/**
 * Button Component
 *
 * Reusable button with consistent styling and variants.
 * Replaces repeated long className strings for buttons across the app.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Button content
 * @param {'primary'|'secondary'|'danger'|'ghost'} [props.variant] - Visual style
 * @param {boolean} [props.fullWidth] - Whether button takes full width
 * @param {boolean} [props.disabled] - Disabled state
 * @param {string} [props.className] - Additional classes to merge
 * @param {object} [props.rest] - Passed through to the button element
 */
import PropTypes from 'prop-types';

const VARIANT_CLASSES = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
  secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-blue-500',
  danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
  ghost: 'text-gray-700 hover:bg-gray-100 focus:ring-gray-500',
};

export default function Button({
  children,
  variant = 'primary',
  fullWidth = false,
  disabled = false,
  className = '',
  ...rest
}) {
  const base = 'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  const variantClass = VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary;
  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <button
      disabled={disabled}
      className={`${base} ${variantClass} ${widthClass} ${className}`.trim()}
      {...rest}
    >
      {children}
    </button>
  );
}

Button.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'ghost']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};
